/**
 * frontend/src/pages/valuation/_valuationIntakeHelpers.ts
 *
 * Pure derivations for ValuationIntake.tsx, split out under
 * docs/FILE_SIZE_POLICY.md ahead of the D2/D3/D4 draft wiring landing on
 * that screen — extracted before the edit rather than after, since the
 * pattern from ValuationTuning.tsx (see _valuationTuningHelpers.ts) is to
 * keep the screen file itself in the safe band, not race the 400-line stop.
 *
 * No React, no state — `buildIntakeBody` is re-run from the screen on
 * submit, it does not own anything itself.
 */
import type { DriverQuestion, RunBody } from '@/types/valuation'
import type { DraftInputs, SaveDraftBody } from '@/types/valuationDraft'
import type { RatesValues } from './_RatesUsed'

// VAL-INTAKE-BLANK-JUDGMENT-1 — the four rates a valuator must judge start
// blank; years and cap weight keep their defaults.
// VAL-INTAKE-HYDRATE-ALL-1 — defaults exist once, used by both the `useState`
// initialisers below and `startClean()`, so the two can never drift apart.
export const INTAKE_DEFAULTS = {
  riskFree: '',
  equityRiskPremium: '',
  sizePremium: '',
  companySpecificRisk: '',
  longTermGrowth: '',
  baseCashFlow: '',
  replacementCompensation: '',
  growthRate: '',
  // VAL-WIRE-CONSISTENCY-1 R1: blank stays blank — Run is blocked, by name, until typed.
  projectionYears: '',
  capWeight: '',
}

export type HydrateKey = 'risk_free_rate' | 'company_specific_risk' | 'long_term_growth_rate'
  | 'equity_risk_premium' | 'size_premium' | 'growth_rate' | 'projection_years'
  | 'capitalized_earnings_weight' | 'base_cash_flow' | 'replacement_compensation'

/** Blank means "not supplied" — never coerced to 0. */
export function num(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

/** The wage is needed unless earnings are known to be at or above the crossover. */
export function replacementCompensationRequired(normalizedEarnings: string, crossover: number): boolean {
  const earnings = num(normalizedEarnings)
  return earnings === null || earnings < crossover
}

/**
 * VAL-ACTOR-JOURNEY-1-SIGNER slice C, R4 — the 5 unconditionally required
 * fields `buildIntakeBody` checks (in the same order), but collecting every
 * miss instead of stopping at the first. `id` matches the `Field` id on
 * ValuationIntake.tsx so the caller can scroll straight to it.
 */
export function getMissingRequiredFields(args: {
  riskFree: string
  companySpecificRisk: string
  longTermGrowth: string
  normalizedEarnings: string
  growthRate: string
  replacementCompensation: string
  crossover: number
  projectionYears: string
  capWeight: string
}): { id: string; label: string }[] {
  const checks: { id: string; label: string; raw: string }[] = [
    { id: 'normalized_earnings', label: 'Normalized earnings', raw: args.normalizedEarnings },
    { id: 'risk_free_rate', label: 'Risk-free rate', raw: args.riskFree },
    { id: 'company_specific_risk', label: 'Company-specific risk', raw: args.companySpecificRisk },
    { id: 'long_term_growth_rate', label: 'Long-term growth rate', raw: args.longTermGrowth },
    { id: 'growth_rate', label: 'DCF growth rate', raw: args.growthRate },
    { id: 'projection_years', label: 'Projection years', raw: args.projectionYears },
    { id: 'capitalized_earnings_weight', label: 'Capitalized-earnings weight', raw: args.capWeight },
  ]
  const missing = checks.filter(c => num(c.raw) === null).map(({ id, label }) => ({ id, label }))
  // VAL-JOURNEY-FIX-2: below the signed SDE/EBITDA crossover, the engine
  // requires replacement owner compensation (core/valuation/income_leg.py:81-91)
  // — list it here too, or the user hits a raw 400 the list didn't warn about.
  // QA-13: blank earnings count as "not yet at the crossover", so it is listed
  // from the first render instead of only after earnings are typed.
  if (replacementCompensationRequired(args.normalizedEarnings, args.crossover) && num(args.replacementCompensation) === null) {
    missing.push({ id: 'replacement_compensation', label: 'Replacement owner compensation' })
  }
  return missing
}

export function buildIntakeBody(args: {
  riskFree: string
  equityRiskPremium: string
  sizePremium: string
  companySpecificRisk: string
  longTermGrowth: string
  normalizedEarnings: string
  replacementCompensation: string
  baseCashFlow: string
  growthRate: string
  projectionYears: string
  capWeight: string
  compMultiples: string
  totalAssets: string
  totalLiabilities: string
  fairValueAdjustments: string
  driverAnswers: Record<string, string>
  questions: DriverQuestion[]
}): RunBody | string {
  const {
    riskFree, equityRiskPremium, sizePremium, companySpecificRisk, longTermGrowth,
    normalizedEarnings, replacementCompensation, baseCashFlow, growthRate, projectionYears, capWeight,
    compMultiples, totalAssets, totalLiabilities, fairValueAdjustments,
    driverAnswers, questions,
  } = args

  const rf = num(riskFree)
  const csr = num(companySpecificRisk)
  const ltg = num(longTermGrowth)
  const earnings = num(normalizedEarnings)
  const growth = num(growthRate)
  if (rf === null) return 'Risk-free rate is required.'
  if (csr === null) return 'Company-specific risk premium is required.'
  if (ltg === null) return 'Long-term growth rate is required.'
  if (earnings === null) return 'Normalized earnings are required.'
  if (growth === null) return 'DCF growth rate is required.'
  const years = num(projectionYears)
  const weight = num(capWeight)
  if (years === null) return 'Projection years is required.'
  if (weight === null) return 'Capitalized-earnings weight is required.'

  // Optional. Omitted, the signed industry profile supplies the market leg
  // (`plan.market_leg` hands the engine [low, anchor, high]), so an empty box
  // is the normal path — not a missing input to be nagged about.
  const comps = compMultiples
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(Number)
  if (comps.some(c => !Number.isFinite(c))) return 'Comparable multiples must be numbers, comma-separated.'

  // VAL-INTAKE-SCOPE-1, ADDENDUM: the driver leg's REQUIRED questions
  // (`question.required`, config data) are mandatory, unconditionally — this
  // mirrors score_questionnaire() itself, which resolves a driver from its
  // required answers alone (S2 fence: corroborating answers never move the
  // score), and a score computed from an incomplete required set is not a
  // defensible one. Corroborating questions never block; they ride along on
  // the wire if answered.
  const requiredQuestions = questions.filter(q => q.required)
  const missingRequired = requiredQuestions.filter(
    q => (driverAnswers[q.question_id] ?? '') === '',
  )
  if (missingRequired.length > 0) {
    return `Answer all ${requiredQuestions.length} required value-driver questions (${missingRequired.length} remaining)`
  }
  const answers: Record<string, string> = {}
  for (const q of questions) {
    const value = driverAnswers[q.question_id]
    if (value) answers[q.question_id] = value
  }

  return {
    risk_free_rate: rf,
    company_specific_risk: csr,
    long_term_growth_rate: ltg,
    equity_risk_premium: num(equityRiskPremium),
    size_premium: num(sizePremium),
    normalized_earnings: earnings,
    // VAL-INCOME-BASIS-FIX-1: blank is allowed here — the engine, not this
    // client, decides whether it's required (400 below the signed crossover).
    replacement_compensation: num(replacementCompensation),
    base_cash_flow: num(baseCashFlow),
    growth_rate: growth,
    projection_years: years,
    capitalized_earnings_weight: weight,
    comp_multiples: comps.length > 0 ? comps : null,
    total_assets: num(totalAssets),
    total_liabilities: num(totalLiabilities),
    fair_value_adjustments: num(fairValueAdjustments),
    driver_answers: Object.keys(answers).length > 0 ? answers : null,
  }
}

/** Blank is `null` (the user cleared it); a box that does not parse is named in `problems`. */
function parsedNumber(raw: string, label: string, problems: string[]): { ok: boolean; value: number | null } {
  const value = num(raw)
  if (value === null && raw.trim() !== '') { problems.push(label); return { ok: false, value } }
  return { ok: true, value }
}

/**
 * VAL-PATH-ADJUST P4b — what step 3 (Adjustments and rates) autosaves: the
 * four legs and nothing else. A box that does not parse is OMITTED and named
 * in `problems`, so a typo never erases the saved value. Earnings, the asset
 * floor and the rates are written by the server (auto-apply) or the signer.
 */
export function legsAutosavePatch(a: {
  replacementCompensation: string; baseCashFlow: string; compMultiples: string; fairValueAdjustments: string
}): { patch: SaveDraftBody; problems: string[] } {
  const patch: Record<string, unknown> = {}
  const problems: string[] = []
  const rc = parsedNumber(a.replacementCompensation, 'Replacement owner compensation', problems)
  if (rc.ok) patch.replacement_compensation = rc.value
  const bcf = parsedNumber(a.baseCashFlow, 'Base cash flow', problems)
  if (bcf.ok) patch.base_cash_flow = bcf.value
  const comps = a.compMultiples.split(',').map(s => s.trim()).filter(Boolean).map(Number)
  if (comps.some(c => !Number.isFinite(c))) problems.push('Comparable multiples')
  else patch.comp_multiples = comps.length > 0 ? comps : null
  const adj = parsedNumber(a.fairValueAdjustments, 'Fair-value adjustments', problems)
  if (adj.ok) patch.fair_value_adjustments = adj.value
  return { patch: patch as SaveDraftBody, problems }
}

/**
 * VAL-SAVE-ON-TYPE-1A — what Intake autosaves. VAL-PATH-ADJUST P4b: only
 * what Intake owns. VAL-PATH-SIGNER P7b: that is the driver answers alone —
 * the multiple override moved to Methodology and rates (overrideAutosavePatch).
 * The legs, rates, earnings and asset floor belong to step 3 and are never
 * sent from here, so an Intake save can never overwrite them with stale values.
 * `benchmark_key` is the caller's to add (first save only).
 */
export interface IntakeAutosaveArgs {
  driverAnswers: Record<string, string>
  hasDraft?: boolean
}

export function intakeAutosavePatch(a: IntakeAutosaveArgs): { patch: SaveDraftBody; problems: string[] } {
  const patch: Record<string, unknown> = {}
  const problems: string[] = []
  patch.driver_answers = a.driverAnswers // `{}` clears the saved answers
  return { patch: patch as SaveDraftBody, problems }
}

/** The multiple override as saved: a multiple needs its reason; blank clears all three. */
export function multipleOverridePatch(
  a: { overrideMultiple: string; overrideRationale: string; overrideDisposition: string },
  problems: string[],
): SaveDraftBody {
  const multiple = parsedNumber(a.overrideMultiple, 'Multiple override', problems)
  const reason = a.overrideRationale.trim()
  if (multiple.ok && multiple.value !== null && reason === '') { problems.push('Multiple override reason'); return {} }
  if (!multiple.ok) return {}
  return {
    override_multiple: multiple.value,
    override_rationale: multiple.value === null ? null : reason,
    override_disposition: multiple.value === null ? null : ((a.overrideDisposition || null) as SaveDraftBody['override_disposition']),
  }
}

/** VAL-PATH-SIGNER P7b — what Methodology and rates autosaves for the
 * multiple override (moved off Intake). A multiple without its reason is
 * omitted and named in `problems`. */
export function overrideAutosavePatch(
  a: { overrideMultiple: string; overrideRationale: string; overrideDisposition: string },
): { patch: SaveDraftBody; problems: string[] } {
  const problems: string[] = []
  return { patch: multipleOverridePatch(a, problems), problems }
}

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))

/** VAL-PATH-ADJUST P4b — the saved rates as the Rates used boxes show them. */
export function ratesFrom(d: DraftInputs | null): RatesValues {
  return {
    riskFree: str(d?.risk_free_rate),
    companySpecificRisk: str(d?.company_specific_risk),
    equityRiskPremium: str(d?.equity_risk_premium),
    sizePremium: str(d?.size_premium),
    longTermGrowth: str(d?.long_term_growth_rate),
    growthRate: str(d?.growth_rate),
    projectionYears: str(d?.projection_years),
    capWeight: str(d?.capitalized_earnings_weight),
  }
}

/** VAL-PATH-SIGNER P7b — what the signer's Rates used autosaves. A box that
 * does not parse is OMITTED and named in `problems`, as on step 3. */
export function ratesAutosavePatch(r: RatesValues, problems: string[]): SaveDraftBody {
  const patch: Record<string, unknown> = {}
  const fields: Array<[keyof RatesValues, string, string]> = [
    ['riskFree', 'risk_free_rate', 'Risk-free rate'],
    ['companySpecificRisk', 'company_specific_risk', 'Company-specific risk'],
    ['equityRiskPremium', 'equity_risk_premium', 'Equity risk premium'],
    ['sizePremium', 'size_premium', 'Size premium'],
    ['longTermGrowth', 'long_term_growth_rate', 'Long-term growth rate'],
    ['growthRate', 'growth_rate', 'DCF growth rate'],
    ['projectionYears', 'projection_years', 'Projection years'],
    ['capWeight', 'capitalized_earnings_weight', 'Capitalized-earnings weight'],
  ]
  for (const [key, wire, label] of fields) {
    const parsed = parsedNumber(r[key], label, problems)
    if (parsed.ok) patch[wire] = parsed.value
  }
  return patch as SaveDraftBody
}
